import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRootAPI } from "@/api";

type Props = {
  businessId?: string;
  userIsDemo: boolean;
  inactiveTab: boolean;
};

export function OrganizationSyncControl({ businessId, userIsDemo, inactiveTab }: Props) {
  const api = useRootAPI();
  const queryClient = useQueryClient();
  const [now, setNow] = React.useState(Date.now());
  const [startError, setStartError] = React.useState<{ lastFinishedJobId?: number } | null>(null);
  const seenStatus = React.useRef(false);
  const lastFinishedJobId = React.useRef<number | undefined>(undefined);

  React.useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const statusQuery = useQuery({
    queryKey: ["organization-users-sync"],
    queryFn: () => api.getOrganizationUsersSync(),
    enabled: !userIsDemo,
    refetchInterval: 5000,
    meta: { preventDefaultErrorHandling: true },
  });

  const start = useMutation({
    mutationFn: () => api.postOrganizationUsersSync(),
    onMutate: () => setStartError(null),
    onSuccess: (status) => {
      queryClient.setQueryData(["organization-users-sync"], status);
    },
    onError: () => setStartError({ lastFinishedJobId: statusQuery.data?.last_finished_job_id }),
    meta: { preventDefaultErrorHandling: true },
  });

  React.useEffect(() => {
    if (
      startError &&
      statusQuery.isSuccess &&
      (statusQuery.data.active_source === "manual" ||
        statusQuery.data.manual_sync_queued ||
        (statusQuery.data.last_result_source === "manual" &&
          statusQuery.data.last_finished_job_id !== startError.lastFinishedJobId))
    ) {
      setStartError(null);
    }
  }, [startError, statusQuery.isSuccess, statusQuery.data]);

  const rejectedStart = start.data?.manual_sync_started === false ? start.data : undefined;
  const resetStart = start.reset;
  React.useEffect(() => {
    if (!rejectedStart || !statusQuery.data) return;
    if (
      statusQuery.data.status !== rejectedStart.status ||
      statusQuery.data.job_id !== rejectedStart.job_id ||
      statusQuery.data.last_finished_job_id !== rejectedStart.last_finished_job_id
    ) {
      resetStart();
    }
  }, [rejectedStart, statusQuery.data, resetStart]);

  React.useEffect(() => {
    if (!statusQuery.data) return;
    const finishedID = statusQuery.data.last_finished_job_id;
    if (
      seenStatus.current &&
      finishedID !== undefined &&
      finishedID !== lastFinishedJobId.current
    ) {
      if (businessId) {
        const key = inactiveTab
          ? ["accounts", businessId, userIsDemo]
          : ["users", businessId, userIsDemo];
        void queryClient.invalidateQueries({ queryKey: key });
      }
    }
    seenStatus.current = true;
    lastFinishedJobId.current = finishedID;
  }, [statusQuery.data, businessId, inactiveTab, userIsDemo, queryClient]);

  if (userIsDemo) return null;

  const status = statusQuery.data;
  const waitMs = status?.next_allowed_manual_sync_at
    ? Math.max(0, new Date(status.next_allowed_manual_sync_at).getTime() - now)
    : 0;
  const waitMinutes = Math.ceil(waitMs / 60000);
  const lastSyncFailed = status?.last_result === "failed";
  const visibleFailure = status?.status === "idle" && lastSyncFailed;
  const manualStartRejected = start.data?.manual_sync_started === false;
  const rejectedLabel =
    status?.status === "running"
      ? "Another refresh started first. No new refresh was queued."
      : status?.active_source === "manual"
        ? "A refresh was already queued."
        : waitMs > 0
          ? `Refresh is available in ${waitMinutes} min.`
          : "Refresh did not start. Check the status and try again.";
  const retryAt = status?.next_retry_at
    ? new Date(status.next_retry_at).toLocaleString()
    : undefined;
  const lastStartedAt = status?.next_allowed_manual_sync_at
    ? new Date(new Date(status.next_allowed_manual_sync_at).getTime() - 15 * 60_000)
    : undefined;
  const lastRunLabel = lastStartedAt
    ? lastStartedAt.toDateString() === new Date(now).toDateString()
      ? lastStartedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
      : lastStartedAt.toLocaleDateString()
    : undefined;
  const label = statusQuery.isPending
    ? "Checking refresh…"
    : statusQuery.isError
      ? "Refresh status unavailable"
      : status?.status === "running"
        ? status.manual_sync_queued
          ? "Refreshing accounts; your refresh is queued"
          : "Refreshing accounts…"
        : status?.status === "queued"
          ? status.manual_sync_queued
            ? "Your refresh is queued"
            : retryAt
              ? `Automatic retry ${retryAt}`
              : "Refresh queued"
          : visibleFailure
            ? lastRunLabel
              ? `Failed · ${lastRunLabel}`
              : "Last refresh failed"
            : lastRunLabel
              ? `Last run ${lastRunLabel}`
              : "Refresh available";
  const statusColor =
    statusQuery.isError || visibleFailure
      ? "text-red-600"
      : status?.status === "idle" && status.last_result === "completed"
        ? "text-green-700"
        : "text-primaryColor";
  const cooldownProgress = Math.min(1, Math.max(0, 1 - waitMs / (15 * 60_000)));
  const canRefresh = !!status?.can_start_manually && !start.isPending && waitMs === 0;

  return (
    <div
      className="flex flex-wrap items-center justify-end gap-2 text-sm"
      role="status"
      aria-live="polite"
    >
      <span className={statusColor} aria-hidden="true">
        {status?.status === "running"
          ? "●"
          : status?.status === "queued"
            ? "◷"
            : lastSyncFailed
              ? "●"
              : "✓"}
      </span>
      <span
        className={statusQuery.isError || visibleFailure ? "text-red-600" : "text-feintGray"}
        title={lastStartedAt ? `Last run: ${lastStartedAt.toLocaleString()}` : undefined}
      >
        {label}
      </span>
      {manualStartRejected && <span className="text-feintGray">{rejectedLabel}</span>}
      {statusQuery.isError ? (
        <button
          type="button"
          className="text-primaryColor underline"
          onClick={() => void statusQuery.refetch()}
        >
          Retry
        </button>
      ) : (
        <button
          type="button"
          className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-primaryColor hover:bg-purple-50 disabled:cursor-not-allowed"
          disabled={!canRefresh}
          onClick={() => start.mutate()}
          aria-label={
            waitMs > 0 ? `Refresh available in ${waitMinutes} minutes` : "Refresh accounts"
          }
          title={
            waitMs > 0
              ? `Next refresh: ${new Date(status!.next_allowed_manual_sync_at!).toLocaleString()}`
              : "Refresh accounts now"
          }
        >
          {waitMs > 0 ? (
            <svg viewBox="0 0 36 36" className="absolute inset-0 h-full w-full" aria-hidden="true">
              <circle
                cx="18"
                cy="18"
                r="15"
                fill="none"
                stroke="currentColor"
                strokeOpacity="0.15"
                strokeWidth="3"
              />
              <circle
                cx="18"
                cy="18"
                r="15"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeDasharray={30 * Math.PI}
                strokeDashoffset={30 * Math.PI * (1 - cooldownProgress)}
                transform="rotate(-90 18 18)"
              />
            </svg>
          ) : (
            <svg
              viewBox="0 0 24 24"
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 11a8 8 0 1 0-2.3 6.7" />
              <path d="M20 4v7h-7" />
            </svg>
          )}
          {waitMs > 0 && <span className="text-[10px] font-semibold">{waitMinutes}m</span>}
        </button>
      )}
      {startError && (
        <span className="text-red-600">Could not confirm refresh. Check status and retry.</span>
      )}
    </div>
  );
}
